import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { AdminMemberView } from '../data-models/admin-member-view.model';
import { MemberListResponse } from '../response-models/member-list-response.model';
import { MemberStats } from '../response-models/member-stats.model';
import { adminOrgPrefix } from '../utils/admin-org-path-utils';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/** Which members to list. */
export type MemberStatusFilter = 'active' | 'inactive' | 'all';

/**
 * An organization's memberships: `/admin/organizations/:id/memberships[/:profileId[/reactivate]]` (hub
 * `admin-organization-memberships-controller.ts`). Owner and Chief Supervisor are organization fields, set
 * through `OrganizationService`; invitations live in `OrganizationInvitationService`.
 * Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationMembershipService extends BaseService<AdminMemberView> {
  constructor() {
    super('memberships', AdminMemberView);
  }

  /** The organization's members and stats. `status` filters the members; the stats always cover everyone. */
  getOrganizationMembers(organizationId: string, status: MemberStatusFilter = 'active'): Promise<MemberListResponse> {
    const query = { status: status === 'all' ? undefined : status };
    return firstValueFrom(this.request('GET', { prefix: adminOrgPrefix(organizationId), query }, data => this.toList(data)));
  }

  /** PUT .../memberships/:profileId: sets the Pilot (1) / PIC (2) bits. */
  async updateMemberRole(organizationId: string, profileId: string, role: number): Promise<void> {
    const opts = { prefix: adminOrgPrefix(organizationId), id: profileId };
    await firstValueFrom(this.request('PUT', opts, () => undefined, { role }));
  }

  /** DELETE .../memberships/:profileId (soft delete). */
  async removeMember(organizationId: string, profileId: string): Promise<void> {
    await firstValueFrom(this.delete(profileId, { prefix: adminOrgPrefix(organizationId) }));
  }

  /** POST .../memberships/:profileId/reactivate */
  async reactivateMember(organizationId: string, profileId: string): Promise<void> {
    const opts = { prefix: adminOrgPrefix(organizationId), id: profileId, path: 'reactivate' };
    await firstValueFrom(this.request('POST', opts, () => undefined, {}));
  }

  private toList(data: unknown): MemberListResponse {
    const source = isRecord(data) ? data : {};
    const members = Array.isArray(source['members']) ? source['members'].map(item => this.createType(item)) : [];
    return { members, stats: toMemberStats(source['stats']) };
  }
}

function toMemberStats(data: unknown): MemberStats {
  const source = isRecord(data) ? data : {};
  return { total: countOrZero(source['total']), active: countOrZero(source['active']), inactive: countOrZero(source['inactive']) };
}
